/* eslint-disable react-refresh/only-export-components */

import {
  useMemo,
  useContext,
  createContext,
  useState,
  FC,
  PropsWithChildren,
} from 'react';

import { createContextualCan } from '@casl/react';
import { defineAbility } from '@casl/ability';
import c, { STATUS } from 'utils/constants';

export const RolePermissionContext = createContext({ state: {} });
export const useRolePermissionContext = () =>
  useContext<{
    state: {
      [key: string]: any;
    };
    dispatch?: React.Dispatch<React.SetStateAction<object>>;
  }>(RolePermissionContext);

export function useValidateRolePermission(
  rolePermissions: any,
  acl: any,
  status = true,
) {
  if (
    rolePermissions &&
    acl &&
    !acl
      .map((key: string) => {
        if (!rolePermissions[key]) return false;

        if (status && rolePermissions[key].status === STATUS.INACTIVE)
          return false;

        if (
          !c.ACL_ACCESS_LEVEL_DEFAULT.includes(rolePermissions[key].accessLevel)
        )
          return false;

        return true;
      })
      .filter((res: any) => Boolean(res)).length
  )
    return false;

  return true;
}

export function ValidateRolePermission(acl: any) {
  const {
    state: { rolePermissions },
  } = useRolePermissionContext();

  return useValidateRolePermission(rolePermissions, acl);
}

export const AbilityContext = createContext<any>({});
export const Can = createContextualCan(AbilityContext.Consumer);

export const useAbilityContext = () => useContext(AbilityContext);

export const Ability: React.FC<PropsWithChildren> = ({ children }) => {
  const {
    state: { rolePermissions },
  } = useRolePermissionContext();

  const ability = defineAbility((can) => {
    if (rolePermissions) {
      Object.keys(rolePermissions).forEach((key) =>
        [
          ...rolePermissions[key].actions,
          rolePermissions[key].status,
          rolePermissions[key].accessLevel,
        ].forEach((permission) => {
          can(permission, key);
        }),
      );
    }
  });

  return (
    <AbilityContext.Provider value={ability}>
      {children}
    </AbilityContext.Provider>
  );
};

export const useCan = (
  action: keyof typeof c.ACTIONS | keyof typeof c.ACCESS_LEVEL,
  permission: (typeof c.PERMISSION_KEYS)[keyof typeof c.PERMISSION_KEYS],
) => {
  const ability = useContext(AbilityContext);

  return ability.can(action, permission);
};

export const useGetPermissionStatus = (
  permission: keyof typeof c.PERMISSION_KEYS,
) => {
  const { state } = useRolePermissionContext();

  if (!state?.rolePermissions) return false;

  return (
    state?.rolePermissions[c.PERMISSION_KEYS[permission]]?.status ===
    STATUS.ACTIVE
  );
};

export const useGetPermissionAccessProperties = (
  permission: keyof typeof c.PERMISSION_KEYS,
) => {
  const { state } = useRolePermissionContext();

  if (!state?.rolePermissions) return {};

  return state?.rolePermissions[c.PERMISSION_KEYS[permission]];
};

const RolePermission: FC<PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useState({});

  const value = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return (
    <RolePermissionContext.Provider value={value}>
      <Ability>{children}</Ability>
    </RolePermissionContext.Provider>
  );
};

export default RolePermission;
